import { useGetTemplatesQuery } from 'store/services/helios/templates';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import type { TemplateType } from 'types';
import { type ModalsType } from 'types';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Table } from 'components/common/Table/Table';
import { heliosTemplatesDataScheme } from 'utils/table/admin/helios/templates';

import { transformDataByDataSchema } from 'utils/common';
import { useTableQueryControls } from 'utils/hooks';
import { TextField } from 'components/mui/common/TextField';

interface Props extends ModalsType {
  defaultSelectedId?: string | null;
  onSubmit: (templateId: string[]) => void;
  datasourceId?: string;
}

export const SelectOverviewTemplateModal = memo(({ defaultSelectedId, open, onClose, onSubmit, datasourceId }: Props) => {
  const { t } = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string[]>([]);
  const [localSearch, setLocalSearch] = useState('');
  const { state, setSort, setPagination, setSearch } = useTableQueryControls('heliosTemplatesSelectOverviewModal', {
    pagination: {
      page: 1,
      pageSize: 10,
    },
    filters: {},
    sort: {
      order: 'asc',
      by: 'id',
    },
    search: '',
  });

  const { data, isFetching } = useGetTemplatesQuery({
    page: state?.pagination.page ?? 1,
    pageSize: state?.pagination.pageSize ?? 10,
    search: state?.search ?? '',
    orderDesc: state?.sort?.order === 'desc',
    orderBy: state?.sort?.by ?? 'id',
    templateType: 'CodeList' as TemplateType,
    status: 'Active',
    datasourceId: datasourceId,
  });

  const transformedData = useMemo(() => {
    return transformDataByDataSchema(data?.items || [], heliosTemplatesDataScheme).map((item) => ({
      ...item,
      templateType: t(`helios.templates.settings.type.${item.templateType.toLowerCase()}`),
      dataSource: item.datasourceName,
    }));
  }, [data?.items, t]);

  const setSearchRef = useRef<(search: string) => void>();
  setSearchRef.current = setSearch;

  const debouncedSetSearch = useMemo(() => debounce((value: string) => setSearchRef.current?.(value), 300), [setSearchRef]);

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleOnSubmit = useCallback(
    (templateId: string[]) => {
      onSubmit(templateId);
      onClose();
    },
    [onClose, onSubmit]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const defaultSelectedRows = useMemo(() => {
    return defaultSelectedId ? [defaultSelectedId] : [];
  }, [defaultSelectedId]);

  return (
    <ActionModal
      open={open}
      title=""
      onClose={onClose}
      onCancel={onClose}
      onSubmit={() => selectedTemplateId && handleOnSubmit(selectedTemplateId)}
      maxWidth="lg"
      submitText={t('helios.functions.addFunctionParametersModal.buttons.select')}>
      <Table
        isLoading={isFetching}
        dataSchema={heliosTemplatesDataScheme}
        data={transformedData}
        controls={<TextField sx={{ marginTop: '20px' }} placeholder={t('common.search._')} value={localSearch} onChange={handleSearchChange} />}
        unableSortFor={['moduleName', 'modifiedDateTime', 'datasourceName']}
        sort={{
          column: state?.sort?.by ?? 'id',
          direction: state?.sort?.order ?? 'asc',
        }}
        defaultSelectedRows={defaultSelectedRows}
        isMultiSelect={false}
        onSelectRow={(row) => setSelectedTemplateId(row)}
        onSort={({ column, direction }) =>
          setSort({
            by: column,
            order: direction,
          })
        }
        page={data?.page ?? 1}
        pageSize={data?.pageSize ?? 10}
        rows={data?.totalCount ?? 0}
        onPageSizeChange={(pageSize) => {
          setPagination({
            page: 1,
            pageSize,
          });
        }}
        onCurrentPageChange={(page) => {
          setPagination({
            page,
            pageSize: state?.pagination.pageSize ?? 10,
          });
        }}
      />
    </ActionModal>
  );
});
